/**
 * Chat User Settings Form
 * User preferences for chat module - personality/tone, plus the read-only
 * effective AI model. The model itself is chosen per chat (ChatModelSelector →
 * chats.model_category), not per user, so this form offers no picker for it.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsRestoring } from '@tanstack/react-query';
import { Loader2, Sparkles } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { Separator } from '../../../components/ui/separator';
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { EntityLoadState, BidiText, EmptyState } from '../common';
import { useApiError } from '../../hooks/useApiError';
import { useNotification } from '../../context/NotificationContext';
import { useChatUserSettings, useModelCategories } from '../../admin/hooks/useChatSettings';
import { usePromptConfig, useUpdatePromptConfig } from '../../ai_chat/hooks/useChat';
import type { PromptConfig } from '../../ai_chat/services/chatApi';

type PartMode = 'none' | 'preset' | 'custom';

// Only show personality and tone parts for user customization
const USER_CUSTOMIZABLE_PARTS = ['personality', 'tone'];

interface ChatUserSettingsFormProps {
  onSuccess?: () => void;
}

export function ChatUserSettingsForm({ onSuccess }: ChatUserSettingsFormProps) {
  const { t } = useTranslation(['aiChat', 'common']);
  const { showSuccess } = useNotification();
  const { handleMutationError } = useApiError();

  // Effective model (read-only — the model is chosen per chat, not per user)
  const { data: settings, isLoading, error } = useChatUserSettings();
  const { data: categoriesData, isLoading: categoriesLoading } = useModelCategories();

  // Prompt config (personality/tone) settings
  const {
    data: promptConfigResponse,
    isLoading: promptConfigLoading,
    error: promptConfigError,
    refetch: refetchPromptConfig,
  } = usePromptConfig();
  // A pending persisted-cache restore leaves `data` undefined with `isLoading`
  // false, which would otherwise render the "no personality options" empty state
  // over settings the user actually has.
  const isRestoring = useIsRestoring();
  const updatePromptConfigMutation = useUpdatePromptConfig();
  // Only the UNSAVED edit lives in state; everything else is read from the query.
  // There is nothing to sync and so no frame to lose: seeding a copy in an effect
  // COMMITTED one painted frame in which the prompt config had loaded and every
  // personality/tone control still showed the empty default — a user's saved tone
  // drawn as "None". `null` means "not edited yet", which is why the fallback is
  // `??` and not `||`: an edit that clears every part is `{}` and must survive.
  const [pendingPromptConfig, setPendingPromptConfig] = useState<PromptConfig | null>(null);

  // Get effective model display text
  const effectiveModelText = useMemo(() => {
    if (!settings?.effective_model_category || !categoriesData?.categories) {
      return null;
    }

    const category = categoriesData.categories.find(c => c.id === settings.effective_model_category);
    if (category?.selected_model) {
      return `${category.name} (${category.selected_model})`;
    }
    return settings.effective_model_category;
  }, [settings, categoriesData]);

  // Filter parts to only show personality and tone
  const promptConfigData = promptConfigResponse?.data;
  const availableParts = useMemo(() => {
    return (promptConfigData?.available_parts || []).filter(
      (part) => USER_CUSTOMIZABLE_PARTS.includes(part.id)
    );
  }, [promptConfigData]);
  const savedPromptConfig = useMemo(() => promptConfigData?.prompt_config || {}, [promptConfigData]);

  /** What the controls render: the unsaved edit if there is one, else the server's. */
  const localPromptConfig = pendingPromptConfig ?? savedPromptConfig;

  // Build a map of presets by ID for quick lookup
  const presetsById = useMemo(() => {
    const map: Record<string, { id: string; name: string; content: string }> = {};
    availableParts.forEach((part) => {
      (part.presets || []).forEach((preset) => {
        map[preset.id] = preset;
      });
    });
    return map;
  }, [availableParts]);

  // Get current state for a prompt part
  const getPartState = (partId: string): { mode: PartMode; presetId: string | null; customText: string } => {
    const partConfig = localPromptConfig[partId];
    if (!partConfig) {
      return { mode: 'none', presetId: null, customText: '' };
    }
    // Check for custom mode - custom key exists (even if empty string)
    if (partConfig.custom !== undefined) {
      return { mode: 'custom', presetId: null, customText: partConfig.custom || '' };
    }
    if (partConfig.preset_id) {
      return { mode: 'preset', presetId: partConfig.preset_id, customText: '' };
    }
    return { mode: 'none', presetId: null, customText: '' };
  };

  // Check if prompt config has unsaved changes
  const promptConfigHasChanges = JSON.stringify(localPromptConfig) !== JSON.stringify(savedPromptConfig);

  // Prompt config handlers
  const handlePartModeChange = (partId: string, newMode: PartMode) => {
    const newConfig = { ...localPromptConfig };
    if (newMode === 'none') {
      delete newConfig[partId];
    } else if (newMode === 'custom') {
      newConfig[partId] = { custom: '' };
    } else {
      // Preset mode - select first available preset
      const part = availableParts.find((p) => p.id === partId);
      const firstPreset = part?.presets?.[0];
      if (firstPreset) {
        newConfig[partId] = { preset_id: firstPreset.id };
      } else {
        delete newConfig[partId];
      }
    }
    setPendingPromptConfig(newConfig);
  };

  const handlePresetChange = (partId: string, presetId: string) => {
    setPendingPromptConfig({
      ...localPromptConfig,
      [partId]: { preset_id: presetId },
    });
  };

  const handleCustomTextChange = (partId: string, text: string) => {
    setPendingPromptConfig({
      ...localPromptConfig,
      [partId]: { custom: text },
    });
  };

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      if (promptConfigHasChanges) {
        await updatePromptConfigMutation.mutateAsync(localPromptConfig);
      }

      showSuccess(t('aiChat:settings.saveSuccess'));
      onSuccess?.();
    } catch (error) {
      // Single-arg: the settings-factory PUTs raise a message-only VALIDATION_ERROR, so there is no field name to map.
      handleMutationError(error);
    }
  };

  if (isLoading || categoriesLoading || promptConfigLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Load failed, or the persisted cache has not settled — either way there is no
  // prompt config to edit, and "no personality options" would be the wrong answer.
  if (promptConfigData === undefined && (error || promptConfigError || isRestoring)) {
    return (
      <EntityLoadState
        data={promptConfigData}
        error={error ?? promptConfigError}
        isLoading={promptConfigLoading}
        onRetry={refetchPromptConfig}
        className="mb-4"
      />
    );
  }

  const isSaving = updatePromptConfigMutation.isPending;

  return (
    <form onSubmit={handleFormSubmit}>
      <div className="space-y-6">
        {categoriesData?.provider && (
          <p className="text-sm text-muted-foreground">
            {t('aiChat:settings.userSettings.provider', { name: categoriesData.provider.name })}
          </p>
        )}

        {effectiveModelText && (
          <p className="text-sm text-muted-foreground">
            {t('aiChat:settings.userSettings.effectiveModel', {
              model: effectiveModelText,
            })}
          </p>
        )}

        {/* Personality & Tone Settings — the prompt parts come from the tenant's
            active prompt-part rows, so a tenant with none gets an empty list and
            would otherwise see nothing but a permanently-disabled Save. */}
        {availableParts.length === 0 && (
          <EmptyState
            compact
            icon={Sparkles}
            message={t('aiChat:settings.noPartsAvailable')}
          />
        )}

        {availableParts.length > 0 && (
          <>
            <Separator className="my-4" />
            <p className="text-sm text-muted-foreground mb-4">
              {t('aiChat:settings.description')}
            </p>

            {availableParts.map((part) => {
              const { mode, presetId, customText } = getPartState(part.id);
              const presets = part.presets || [];
              const selectedPreset = presetId ? presetsById[presetId] : null;

              return (
                <div key={part.id} className="space-y-3">
                  <h4 className="text-sm font-medium"><BidiText>{part.name}</BidiText></h4>
                  {part.description && (
                    <p className="text-sm text-muted-foreground">
                      {part.description}
                    </p>
                  )}

                  <ToggleGroup
                    type="single"
                    value={mode}
                    onValueChange={(value) => value && handlePartModeChange(part.id, value as PartMode)}
                    className="justify-start"
                  >
                    <ToggleGroupItem value="none" size="sm">
                      {t('aiChat:settings.default')}
                    </ToggleGroupItem>
                    <ToggleGroupItem value="preset" size="sm" disabled={presets.length === 0}>
                      {t('aiChat:settings.usePreset')}
                    </ToggleGroupItem>
                    <ToggleGroupItem value="custom" size="sm">
                      {t('aiChat:settings.custom')}
                    </ToggleGroupItem>
                  </ToggleGroup>

                  {mode === 'preset' && (
                    <div className="space-y-2">
                      <Select
                        value={presetId || 'none'}
                        onValueChange={(v) => v !== 'none' && handlePresetChange(part.id, v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('aiChat:settings.selectPreset')} />
                        </SelectTrigger>
                        <SelectContent>
                          {presets.map((preset) => (
                            <SelectItem key={preset.id} value={preset.id}>
                              {preset.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedPreset && (
                        <div className="p-3 bg-muted rounded-md">
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                            {selectedPreset.content}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {mode === 'custom' && (
                    <Textarea
                      value={customText}
                      onChange={(e) => handleCustomTextChange(part.id, e.target.value)}
                      placeholder={t('aiChat:settings.customPlaceholder')}
                      rows={3}
                    />
                  )}
                </div>
              );
            })}
          </>
        )}

        {/* Nothing is editable with no prompt parts, so a permanently-disabled
            Save would just read as a broken form. */}
        {availableParts.length > 0 && (
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="submit"
              disabled={!promptConfigHasChanges || isSaving}
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common:app.save')}
            </Button>
          </div>
        )}
      </div>
    </form>
  );
}
